import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Typography, Row, Col, Statistic, Spin, Alert, Button, Space,
  Divider, Tag, Descriptions, message,
} from 'antd';
import {
  TeamOutlined, FileTextOutlined, AlertOutlined, BookOutlined,
  ThunderboltFilled, ReloadOutlined, FilePdfOutlined,
  RiseOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import { analyticsApi, AnalyticsDashboard, WeeklyReport } from '../../api/analytics';

const { Title, Text } = Typography;

const AnalyticsPage: React.FC = () => {
  const [dashboard, setDashboard] = useState<AnalyticsDashboard | null>(null);
  const [report, setReport] = useState<WeeklyReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await analyticsApi.dashboard();
      setDashboard(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки аналитики');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const res = await analyticsApi.weeklyReport();
      setReport(res.data);
    } catch {
      // Non-critical
    } finally {
      setReportLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); fetchReport(); }, [fetchDashboard, fetchReport]);

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await analyticsApi.weeklyReportPdf();
      if (res.data instanceof Blob) {
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'aqsaiym_weekly_report.pdf';
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        // PDF generation not available — server returned JSON
        const jsonData = res.data as any;
        if (jsonData?.detail) {
          message.info(jsonData.detail);
        }
      }
    } catch {
      message.error('Не удалось сформировать PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const d = dashboard;

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={3} style={{ margin: 0, color: 'var(--l-ink)' }}>Аналитика</Title>
          <Text type="secondary">Обзорная статистика и еженедельный отчёт для акима</Text>
        </div>
        <Space>
          <Button icon={<FilePdfOutlined />} onClick={downloadPdf} loading={pdfLoading}>
            Скачать PDF-отчёт
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => { fetchDashboard(); fetchReport(); }}>
            Обновить
          </Button>
        </Space>
      </div>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Spin spinning={loading}>
        {/* Users */}
        <Title level={5} style={{ marginBottom: 12 }}><TeamOutlined /> Пользователи</Title>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={6}>
            <Card><Statistic title="Всего" value={d?.users?.total ?? 0} prefix={<TeamOutlined style={{ color: 'var(--l-muted)' }} />} /></Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card style={{ }}>
              <Statistic title="Сегодня" value={d?.users?.new_today ?? 0} valueStyle={{ color: 'var(--l-ok)' }} prefix={<RiseOutlined />} />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card style={{ }}>
              <Statistic title="За неделю" value={d?.users?.new_week ?? 0} valueStyle={{ color: 'var(--l-accent)' }} />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card style={{ }}>
              <Statistic title="За месяц" value={d?.users?.new_month ?? 0} valueStyle={{ color: 'var(--l-violet)' }} />
            </Card>
          </Col>
        </Row>

        {/* Appeals */}
        <Title level={5} style={{ marginBottom: 12 }}><AlertOutlined /> Обращения</Title>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={6}>
            <Card><Statistic title="Всего" value={d?.appeals?.total ?? 0} /></Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card style={{ }}>
              <Statistic title="На рассмотрении" value={d?.appeals?.pending ?? 0} valueStyle={{ color: 'var(--l-warn)' }} />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card style={{ }}>
              <Statistic title="В работе" value={d?.appeals?.in_progress ?? 0} valueStyle={{ color: 'var(--l-accent)' }} />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card style={{ }}>
              <Statistic title="Решено" value={d?.appeals?.resolved ?? 0} valueStyle={{ color: 'var(--l-ok)' }} />
            </Card>
          </Col>
        </Row>
        {d?.appeals?.avg_resolution_hours != null && (
          <Card style={{ marginBottom: 24 }}>
            <Statistic
              title="Среднее время решения"
              value={d.appeals.avg_resolution_hours}
              suffix="часов"
              prefix={<ClockCircleOutlined />}
              precision={1}
            />
          </Card>
        )}

        {/* Content */}
        <Title level={5} style={{ marginBottom: 12 }}><FileTextOutlined /> Контент</Title>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={6}>
            <Card><Statistic title="Новости (всего)" value={d?.news?.total ?? 0} prefix={<FileTextOutlined style={{ color: 'var(--l-muted)' }} />} /></Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card style={{ }}>
              <Statistic title="За неделю" value={d?.news?.published_week ?? 0} valueStyle={{ color: 'var(--l-accent)' }} />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card><Statistic title="Справочник (всего)" value={d?.directory?.total ?? 0} prefix={<BookOutlined style={{ color: 'var(--l-muted)' }} />} /></Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card style={{ }}>
              <Statistic title="С координатами" value={d?.directory?.with_location ?? 0} valueStyle={{ color: 'var(--l-ok)' }} />
            </Card>
          </Col>
        </Row>

        {/* Infrastructure */}
        <Title level={5} style={{ marginBottom: 12 }}><ThunderboltFilled /> Инфраструктура</Title>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card style={{ }}>
              <Statistic title="Активные отключения" value={d?.shutoffs?.active ?? 0} valueStyle={{ color: 'var(--l-danger)' }} />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card style={{ }}>
              <Statistic title="Предстоящие отключения" value={d?.shutoffs?.upcoming ?? 0} valueStyle={{ color: 'var(--l-warn)' }} />
            </Card>
          </Col>
          {d?.polls && (
            <Col xs={24} sm={8}>
              <Card style={{ }}>
                <Statistic title="Активные опросы" value={d.polls.active ?? 0} suffix={<Text type="secondary" style={{ fontSize: 14 }}>/ {d.polls.total_votes} голосов</Text>} />
              </Card>
            </Col>
          )}
        </Row>
      </Spin>

      {/* Weekly Report */}
      <Divider />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Еженедельный отчёт для акима</Title>
        <Tag>{report?.period ? `${report.period.start} — ${report.period.end}` : 'Загрузка...'}</Tag>
      </div>
      <Spin spinning={reportLoading}>
        {report ? (
          <Card>
            <Descriptions column={2} bordered size="small">
              {report.users && (
                <>
                  <Descriptions.Item label="Новые пользователи">{report.users.new_this_week ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Всего пользователей">{report.users.total ?? '—'}</Descriptions.Item>
                </>
              )}
              {report.appeals && (
                <>
                  <Descriptions.Item label="Новые обращения">{report.appeals.new_this_week ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Решённые обращения">{report.appeals.resolved_this_week ?? '—'}</Descriptions.Item>
                </>
              )}
              {report.news && (
                <Descriptions.Item label="Опубликовано новостей" span={2}>{report.news.published_this_week ?? '—'}</Descriptions.Item>
              )}
              {report.shutoffs && (
                <>
                  <Descriptions.Item label="Отключений за неделю">{report.shutoffs.total_this_week ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Аварийных">{report.shutoffs.emergency_this_week ?? '—'}</Descriptions.Item>
                </>
              )}
            </Descriptions>
          </Card>
        ) : (
          <Card><Text type="secondary">Данные отчёта загружаются...</Text></Card>
        )}
      </Spin>
    </div>
  );
};

export default AnalyticsPage;
